import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { HttpError, request } from "../../api/http";
import { useAuth } from "../../context/AuthContext";
import { LeadStatus } from "../../types/onboarding";
import { LeadDetail as LeadDetailData } from "../../types/leadDetail";
import Observations from "../OnboardingCustomers/components/Observations";
import CrmLeadPanels from "./components/CrmLeadPanels";
import DuplicateLeads from "./components/DuplicateLeads";
import LeadHeader from "./components/LeadHeader";
import QuickActionsBar from "./components/QuickActionsBar";
import PersonalSection from "./components/PersonalSection";
import {
  BrokerSection,
  CancellationCard,
  FamilyEconomySection,
  HealthSection,
  PurchaseSection,
  TelesubscriptionBanner,
  TrackingSection,
} from "./components/Sections";
import DunningCard from "./components/DunningCard";

export default function LeadDetail() {
  const { leadUlid } = useParams<{ leadUlid: string }>();
  const { token } = useAuth();
  const [detail, setDetail] = useState<LeadDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!leadUlid) return;
    setLoading(true);
    setError(null);
    try {
      const data = await request<LeadDetailData>(
        `/api/admin/operations/leads/${leadUlid}/detail`,
        { token },
      );
      setDetail(data);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(
          err.status === 404
            ? "Lead no encontrado"
            : `Error HTTP ${err.status}`,
        );
      } else {
        setError("No se pudo cargar el lead");
      }
    } finally {
      setLoading(false);
    }
  }, [leadUlid, token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusChange = (newStatus: LeadStatus) => {
    setDetail((prev) => (prev ? { ...prev, status: newStatus } : prev));
  };

  if (loading && !detail) {
    return (
      <div style={{ padding: "1.5rem", color: "#73879C" }}>Cargando lead…</div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: "1.5rem", color: "#b32424" }}>{error}</div>
    );
  }
  if (!detail) {
    return null;
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 360px",
        gap: "1rem",
        padding: "1rem",
        maxWidth: 1600,
        margin: "0 auto",
        alignItems: "start",
      }}
    >
      <main style={{ minWidth: 0 }}>
        <LeadHeader detail={detail} />
        <TelesubscriptionBanner detail={detail} />
        <QuickActionsBar
          detail={detail}
          onStatusChange={handleStatusChange}
          onReload={load}
        />
        <CancellationCard detail={detail} />
        {/*
          El DINERO, junto a la cancelación y antes de los datos del
          cuestionario: cuando el cliente llama, lo primero que hay que ver es
          si debe algo y qué se le ha mandado. Degrada solo si no hay caso.
        */}
        <DunningCard leadUlid={detail.leadUlid} />
        <DuplicateLeads detail={detail} />
        <PersonalSection detail={detail} onReload={load} />
        <PurchaseSection detail={detail} />
        <HealthSection detail={detail} />
        <FamilyEconomySection detail={detail} />
        <BrokerSection detail={detail} />
        <TrackingSection detail={detail} />
      </main>
      <aside
        style={{
          position: "sticky",
          top: "1rem",
          maxHeight: "calc(100vh - 2rem)",
          overflow: "auto",
        }}
      >
        <Observations leadUlid={detail.leadUlid} />
        {/* Los paneles CRM van juntos: CrmLeadPanels resuelve primero la ficha
            canónica del CRM (un lead re-registrado vive bajo su ULID antiguo)
            y reparte ese id a perfil/tickets/link/timeline. */}
        <CrmLeadPanels leadUlid={detail.leadUlid} email={detail.email} />
      </aside>
    </div>
  );
}
